/**
 * Storage shim — Supabase-backed.
 *
 * Replaces the old localStorage shim. The existing tool calls
 * `window.storage.get/set/delete/list(key, shared)` and we route those calls
 * to Supabase tables:
 *   - `rd_study_state_v2` (personal) → engagements.state JSONB for the active engagement
 *   - `rd_tool_feedback_shared`     → feedback table
 *
 * The "active engagement" is tracked in this module. Call setActiveEngagement(id)
 * before rendering the main tool so saves/loads target the right row.
 */

import { supabase } from "./supabase.js";

const ACTIVE_KEY = "rd_active_engagement_id";

let currentEngagementId = null;
let realtimeChannel = null;
let lastSelfWriteAt = 0;

// Restore from localStorage so reloads stay on the same engagement
if (typeof window !== "undefined") {
  try {
    const saved = localStorage.getItem(ACTIVE_KEY);
    if (saved) currentEngagementId = saved;
  } catch (e) {
    /* ignore */
  }
}

export function setActiveEngagement(id) {
  if (id === currentEngagementId && realtimeChannel) return;

  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }

  currentEngagementId = id || null;

  if (typeof window !== "undefined") {
    try {
      if (id) localStorage.setItem(ACTIVE_KEY, id);
      else localStorage.removeItem(ACTIVE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  if (id) {
    realtimeChannel = supabase
      .channel(`engagement:${id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "engagements",
          filter: `id=eq.${id}`,
        },
        (payload) => {
          // Suppress notifications for the user's own writes within 2s
          if (Date.now() - lastSelfWriteAt < 2000) return;
          window.dispatchEvent(
            new CustomEvent("rd-engagement-external-update", {
              detail: payload.new,
            }),
          );
        },
      )
      .subscribe();
  }
}

export function getActiveEngagement() {
  return currentEngagementId;
}

// Lifecycle step keys, in order. Mirrors the engagement state shape
// in RdStudyTool.jsx (the engagement.lifecycle object).
const LIFECYCLE_STEPS = [
  "proposal",
  "letterDrafted",
  "letterSent",
  "letterSigned",
  "retainerReceived",
  "studyStarted",
  "inProgress",
  "completed",
  "delivered",
];

function computeFeeAmount(eng) {
  const fs = eng?.feeStructure || "fixed";
  const fixed = parseFloat(eng?.fixedFee) || 0;
  const rate = parseFloat(eng?.hourlyRate) || 0;
  const hours = parseFloat(eng?.estimatedHours) || 0;
  const cap = parseFloat(eng?.contingentCap) || 0;

  switch (fs) {
    case "fixed":
      return fixed;
    case "hourly":
      return rate * hours;
    case "contingent":
      // Use the cap as the upper-bound display value for the home screen
      return cap;
    case "hybrid":
      return fixed + rate * hours;
    default:
      return fixed;
  }
}

function deriveStatusFromLifecycle(lifecycle) {
  if (!lifecycle) return "not_started";
  let lastCompleted = -1;
  for (let i = LIFECYCLE_STEPS.length - 1; i >= 0; i--) {
    if (lifecycle[LIFECYCLE_STEPS[i]]?.status === "completed") {
      lastCompleted = i;
      break;
    }
  }
  if (lastCompleted === -1) return "not_started";
  if (lastCompleted >= 8) return "delivered";
  if (lastCompleted >= 7) return "review";
  if (lastCompleted >= 5) return "in_progress";
  if (lastCompleted >= 3) return "letter_signed";
  if (lastCompleted >= 1) return "letter_drafted";
  return "proposal_sent";
}

function deriveCompletePercent(lifecycle) {
  if (!lifecycle) return 0;
  const completed = LIFECYCLE_STEPS.filter(
    (s) => lifecycle[s]?.status === "completed",
  ).length;
  return Math.round((completed / LIFECYCLE_STEPS.length) * 100);
}

/**
 * Extracts denormalized fields from the engagement state JSON for the
 * home-page table. Mirrors the field names actually used in
 * RdStudyTool.jsx (engagement.fixedFee, engagement.feeStructure,
 * engagement.timeEntries[].hours, engagement.lifecycle, etc.).
 */
function extractDenormalized(state) {
  const e = state?.engagement || {};
  const s = state?.scope || {};

  const name =
    e.engagementName ||
    e.name ||
    s.clientName ||
    s.client ||
    "Untitled engagement";

  const client_name = s.clientName || e.clientName || null;

  let tax_year = parseInt(s.taxYear, 10);
  if (!Number.isFinite(tax_year) || tax_year < 1900 || tax_year > 2100)
    tax_year = null;

  const entity_type = s.entityType || null;

  const status = deriveStatusFromLifecycle(e.lifecycle);

  const fee_amount = computeFeeAmount(e);
  const fee_type = e.feeStructure || "fixed";

  let total_hours = 0;
  if (Array.isArray(e.timeEntries)) {
    total_hours = e.timeEntries.reduce(
      (sum, t) => sum + (parseFloat(t?.hours) || 0),
      0,
    );
  }

  const percent_complete = deriveCompletePercent(e.lifecycle);

  // Pending items: open client requests + documents awaiting upload
  let pending_items_count = 0;
  if (Array.isArray(state?.requestLists)) {
    pending_items_count += state.requestLists.reduce((acc, list) => {
      if (Array.isArray(list?.items)) {
        return (
          acc +
          list.items.filter(
            (item) => item && !item.completed && !item.received,
          ).length
        );
      }
      return acc;
    }, 0);
  }
  if (Array.isArray(state?.documents)) {
    pending_items_count += state.documents.filter(
      (d) => d && (d.pending === true || d.uploaded === false),
    ).length;
  }

  return {
    name,
    client_name,
    tax_year,
    entity_type,
    status,
    fee_amount,
    fee_type,
    total_hours,
    percent_complete,
    pending_items_count,
  };
}

async function get(key, shared = false) {
  try {
    if (shared && key === "rd_tool_feedback_shared") {
      const { data, error } = await supabase
        .from("feedback")
        .select("content, created_at")
        .order("created_at", { ascending: true });
      if (error) throw error;
      const items = (data || []).map((row) => row.content);
      return { key, value: JSON.stringify(items), shared: true };
    }

    if (!shared && key === "rd_study_state_v2") {
      if (!currentEngagementId) return null;
      const { data, error } = await supabase
        .from("engagements")
        .select("state")
        .eq("id", currentEngagementId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return { key, value: JSON.stringify(data.state || {}), shared: false };
    }

    return null;
  } catch (e) {
    console.error("storage.get error", e);
    return null;
  }
}

async function set(key, value, shared = false) {
  try {
    if (shared && key === "rd_tool_feedback_shared") {
      const newArr = JSON.parse(value);
      if (!Array.isArray(newArr)) return null;

      const { data: existing, error: listErr } = await supabase
        .from("feedback")
        .select("created_at")
        .order("created_at", { ascending: true });
      if (listErr) throw listErr;
      const existingCount = existing?.length || 0;

      const toInsert = newArr.slice(existingCount);
      if (toInsert.length === 0) return { key, value, shared };

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { error } = await supabase.from("feedback").insert(
        toInsert.map((item) => ({
          content: item,
          author_id: user?.id || null,
        })),
      );
      if (error) throw error;
      return { key, value, shared };
    }

    if (!shared && key === "rd_study_state_v2") {
      if (!currentEngagementId) {
        console.error(
          "storage.set: setActiveEngagement(id) must be called before saving state",
        );
        return null;
      }
      const stateObj = JSON.parse(value);
      const denorm = extractDenormalized(stateObj);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      lastSelfWriteAt = Date.now();
      const { error } = await supabase
        .from("engagements")
        .update({
          state: stateObj,
          ...denorm,
          last_edited_by: user?.id || null,
          last_edited_at: new Date().toISOString(),
        })
        .eq("id", currentEngagementId);
      if (error) throw error;
      return { key, value, shared };
    }

    return null;
  } catch (e) {
    console.error("storage.set error", e);
    return null;
  }
}

async function del(key, shared = false) {
  try {
    if (!shared && key === "rd_study_state_v2") {
      if (!currentEngagementId) return null;
      lastSelfWriteAt = Date.now();
      const { error } = await supabase
        .from("engagements")
        .update({ state: {} })
        .eq("id", currentEngagementId);
      if (error) throw error;
      return { key, deleted: true, shared };
    }
    return null;
  } catch (e) {
    console.error("storage.delete error", e);
    return null;
  }
}

async function list() {
  // Not used by the tool; preserved for API parity.
  return { keys: [], prefix: "", shared: false };
}

if (typeof window !== "undefined") {
  window.storage = { get, set, delete: del, list };
}

export default { get, set, delete: del, list, setActiveEngagement, getActiveEngagement };
